import { useCallback, useState } from 'react';
import type { Transcription } from '@frameio/graphql-client/generated';
import { Dialog, DialogHeader } from '@frameio/vapor';
import { GenerateTranscriptionForm } from '../../components/GenerateTranscriptionForm';
import TEXT from './generated/TEXT/useManageGenerateTranscriptionDialog';

const MODAL_WIDTH = 356;
type GenerateTranscriptionsDialogProps = {
  assetIds?: string[] | null;
  isAddLanguageMode?: boolean;
  isShowing: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
  transcriptions?: Transcription[] | undefined;
};
TEXT.DEFINE({
  /** Label for close dialog button */
  dialogCloseLabel: 'Close Dialog',
});

export const useManageGenerateTranscriptionDialog = () => {
  const [isShowing, setIsShowing] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[] | null>(
    null
  );
  const createTranscriptionDialog = useCallback(
    ({
      onClose,
      onSuccess,
      transcriptions,
      isAddLanguageMode,
    }: {
      onClose?: () => void;
    } & Pick<
      GenerateTranscriptionsDialogProps,
      'onSuccess' | 'isAddLanguageMode' | 'transcriptions'
    >) => (
      <GenerateTranscriptionsDialog
        assetIds={selectedAssetIds ?? []}
        isAddLanguageMode={isAddLanguageMode}
        isShowing={isShowing}
        onClose={() => {
          onClose?.();
          setIsShowing(false);
          setSelectedAssetIds(null);
        }}
        onSuccess={onSuccess}
        transcriptions={transcriptions}
      />
    ),
    [isShowing, selectedAssetIds]
  );
  const handleSetIsShowing = (assetIds?: string[]) => {
    if (!assetIds || assetIds.length === 0) return;
    setIsShowing(true);
    setSelectedAssetIds(assetIds);
  };
  return {
    openGenerateTranscriptsDialog: handleSetIsShowing,
    createTranscriptionDialog,
    isShowing,
  };
};
export const GenerateTranscriptionsDialog = ({
  transcriptions,
  assetIds,
  isShowing,
  onClose,
  onSuccess,
  isAddLanguageMode,
}: GenerateTranscriptionsDialogProps) => {
  const handleSuccess = useCallback(
    (id: string) => {
      onSuccess?.(id);
      onClose?.();
    },
    [onClose, onSuccess]
  );
  return (
    <Dialog
      isShowing={isShowing}
      onRequestClose={onClose}
      style={{ maxWidth: `${MODAL_WIDTH}px` }}
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        showBottomBorder={false}
        showCloseButton={true}
        title=""
      />
      <GenerateTranscriptionForm
        activeTranscription={null}
        assetIds={assetIds ?? undefined}
        isAddLanguageMode={isAddLanguageMode}
        onError={onClose}
        onSuccess={handleSuccess}
        showFooter={true}
        transcriptions={transcriptions}
      />
    </Dialog>
  );
};
